/** MI DOMINO - dragging a ficha to the table. HTML5 drag-and-drop (desktop)
 *  onto the drop zones, back onto the rack, or anywhere on the felt — plus the
 *  touch-drag mirror for phones. Same flow offline and ONLINE. */
import { attemptPlace, hideSideChoice, hideZones, placeTile, playableSides, render, showZones, zoneL, zoneR } from './engine';
import { clack } from './sounds';
import { netSides, sendPlay } from '../net/online';
import { NET, S, dragIdx, dragTile, setDragIdx, setDragTile, setPendingTile, setPrevLeftEnd, setPrevRightEnd } from '../state';
import { $ } from '../ui/dom';

/* Play a ficha released at a screen point — the nearest board end decides the
   side, identical geometry offline & online. Shared by the desktop felt drop
   and the phone touch-drag below. */
export function playTileAtPoint(t: any, x: number, y: number): void {
  const nearestSide = () => {
    const kids = $('board').children;
    if (!kids.length) return 'first';
    const fr = kids[0].getBoundingClientRect();
    const lr = kids[kids.length - 1].getBoundingClientRect();
    const dFirst = Math.hypot(x - (fr.left + fr.width / 2), y - (fr.top + fr.height / 2));
    const dLast = Math.hypot(x - (lr.left + lr.width / 2), y - (lr.top + lr.height / 2));
    return dFirst <= dLast ? 'left' : 'right';
  };
  if (NET.active) {
    const s = NET.snap;
    if (!s || s.turn !== s.mySeat) return;      // not your turn
    const legal = netSides(t);
    if (!legal.length) return;                  // dead tile — nothing to drop
    if (!s.board.length) return sendPlay(t, 'first');
    const side = nearestSide();
    return sendPlay(t, legal.includes(side) ? side : legal[0]);
  }
  // offline
  if (!S || S.turn !== 0 || S.handOver || S.over || S.phase !== 'play' || S.busy) return;
  hideSideChoice();
  setPendingTile(null);
  if (S.board.length === 0) return attemptPlace(t, 'first');
  attemptPlace(t, nearestSide());
}

/** Wire every drop target (needs #drop-left/right, #hand, #felt, #board). */
export function initDrag(): void {
  const zones: [any, string][] = [[zoneL, 'left'], [zoneR, 'right']];
  for (const [zone, side] of zones) {
    zone.addEventListener('dragover', (ev: any) => { ev.preventDefault(); zone.classList.add('over'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('over'));
    zone.addEventListener('drop', (ev: any) => {
      ev.preventDefault();
      const t = dragTile; setDragTile(null);
      hideZones();
      if (!t) return;
      if (NET.active) {
        const sides = netSides(t);
        const chosen = sides.includes('first') ? 'first' : side;
        if (chosen !== 'first' && !sides.includes(side)) return;
        hideSideChoice(); setPendingTile(null);
        sendPlay(t, chosen);
        return;
      }
      if (S.turn !== 0 || S.handOver || S.over) return;
      const sides = playableSides(t);
      const chosen = sides.includes('first') ? 'first' : side;
      if (chosen !== 'first' && !sides.includes(side)) return;
      setPrevLeftEnd(S.leftEnd); setPrevRightEnd(S.rightEnd);
      hideSideChoice(); setPendingTile(null);
      placeTile(0, t, chosen);
    });
  }

  /* drop a ficha on the empty rack space to send it to the end of your hand */
  $('hand').addEventListener('dragover', (ev: any) => ev.preventDefault());
  $('hand').addEventListener('drop', (ev: any) => {
    ev.preventDefault();
    if (!S || NET.active || S.phase !== 'play' || dragIdx === null) return;
    const h = S.hands[0];
    const [moved] = h.splice(dragIdx, 1);
    h.push(moved);
    setDragTile(null); setDragIdx(null);
    clack();
    render();
  });

  /* drop your ficha ANYWHERE on the table — nearest end decides the side, no arrows.
     Same flow offline and ONLINE (Jerry: the online table must behave like offline). */
  $('felt').addEventListener('dragover', (ev: any) => {
    ev.preventDefault(); // allow the drop in both offline and online
  });
  $('felt').addEventListener('drop', (ev: any) => {
    ev.preventDefault();
    const t = dragTile;
    setDragTile(null);
    hideZones();
    if (!t) return;
    playTileAtPoint(t, ev.clientX, ev.clientY);
  });

  /* ---------- TOUCH DRAG (phones) ----------
     HTML5 drag-and-drop is desktop-only (no touch), so on a phone the fichas
     couldn't be dragged. This mirrors the desktop drag with touch events: a tap
     still opens the side-choice (onTileClick), but a drag past a small threshold
     picks the ficha up, follows the finger, shows the drop zones, and plays it on
     release over the felt. Delegated on #hand so it survives every re-render. */
  (function () {
    const hand = $('hand');
    if (!hand) return;
    const THRESH = 8;
    let armed = false, dragging = false, ghost: any = null, srcEl: any = null, tile: any = null, sx = 0, sy = 0;

    const parse = (el: any) => (el && el.dataset && el.dataset.tile) ? el.dataset.tile.split(',').map(Number) : null;
    const myTurn = () => NET.active
      ? (NET.snap && NET.snap.turn === NET.snap.mySeat)
      : (S && S.turn === 0 && S.phase === 'play' && !S.handOver && !S.over && !S.busy);

    const moveGhost = (tch: any) => {
      if (!ghost) return;
      const r = ghost.getBoundingClientRect();
      ghost.style.left = (tch.clientX - r.width / 2) + 'px';
      ghost.style.top = (tch.clientY - r.height / 2) + 'px';
    };
    const cleanup = () => {
      if (srcEl) srcEl.style.opacity = '';
      if (ghost) { ghost.remove(); ghost = null; }
      hideZones();
      armed = dragging = false; srcEl = null;
    };

    hand.addEventListener('touchstart', (ev: any) => {
      const el = ev.target.closest && ev.target.closest('.tile');
      if (!el) return;
      const t = parse(el);
      if (!t || !myTurn()) return;
      armed = true; dragging = false; srcEl = el; tile = t;
      sx = ev.touches[0].clientX; sy = ev.touches[0].clientY;
      // no preventDefault yet: let a plain tap still fire onTileClick (side-choice)
    }, { passive: false });

    hand.addEventListener('touchmove', (ev: any) => {
      if (!armed) return;
      const tch = ev.touches[0];
      if (!dragging) {
        if (Math.hypot(tch.clientX - sx, tch.clientY - sy) < THRESH) return;
        dragging = true;
        showZones(tile);                       // respects the hints setting
        ghost = srcEl.cloneNode(true);
        ghost.style.cssText += ';position:fixed;z-index:9999;pointer-events:none;opacity:.92;margin:0;filter:drop-shadow(0 8px 16px rgba(0,0,0,.6));';
        document.body.appendChild(ghost);
        srcEl.style.opacity = '.3';
      }
      ev.preventDefault();                      // we own the gesture now (no scroll, no click)
      moveGhost(tch);
    }, { passive: false });

    hand.addEventListener('touchend', (ev: any) => {
      if (!armed) return;
      const wasDrag = dragging;
      const t = tile;
      const tch = ev.changedTouches[0];
      cleanup();
      if (wasDrag && t && tch) {
        const f = $('felt').getBoundingClientRect();
        const overFelt = tch.clientX >= f.left && tch.clientX <= f.right && tch.clientY >= f.top && tch.clientY <= f.bottom;
        if (overFelt) playTileAtPoint(t, tch.clientX, tch.clientY);
      }
      // a tap (no drag) falls through to onTileClick — side-choice as before
    });
    hand.addEventListener('touchcancel', cleanup);
  })();
}
